import type { PaymentTermGapTransactionDto, RelanceFlagsDto } from '../../types/customerFollowUp/relance';
import { TranslationKey } from '../../i18n/translationKeys';

export interface MissingInformationItem {
    labelKey: string;
    // Optional fields are surfaced in the banner but do not block sending. Only mandatory
    // fields lock the email section.
    optional: boolean;
    // Invoices to drill into for this gap (payment terms only). Empty for customer-level gaps,
    // which are corrected on the customer record instead.
    transactions?: PaymentTermGapTransactionDto[];
}

export interface MissingInformationResult {
    // Fields corrected on the customer record: email, language, customer-level payment term.
    clientItems: MissingInformationItem[];
    // Fields corrected on individual documents: per-transaction payment term gaps.
    transactionItems: MissingInformationItem[];
}

// Single source of truth for what counts as "missing information" on a relance.
// Flags being null means "not loaded yet" → report nothing, which keeps the
// banner from flashing before the data resolves.
export function getMissingInformation(flags: RelanceFlagsDto | null | undefined): MissingInformationResult {
    if (!flags) return { clientItems: [], transactionItems: [] };
    const clientItems: MissingInformationItem[] = [];
    const transactionItems: MissingInformationItem[] = [];
    const documentGaps = flags.paymentTermGapTransactions;

    // Customer-record fields: mandatory first, optional last.
    if (!flags.hasEmail) clientItems.push({ labelKey: TranslationKey.CustomerFollowUp.MissingInfo.Email, optional: false });
    if (!flags.hasLanguage) clientItems.push({ labelKey: TranslationKey.CustomerFollowUp.MissingInfo.Language, optional: true });
    // The customer-level default payment term is optional and suppressed when document-level gaps
    // already surface payment conditions, to avoid a duplicate line.
    if (!flags.hasPaymentTerm && documentGaps.length === 0)
        clientItems.push({ labelKey: TranslationKey.CustomerFollowUp.MissingInfo.PaymentTerm, optional: true });

    // Document-level gaps: each offending invoice/inscription is linked for drilldown. Optional
    // because a document without a payment term is still relanced, as due from its own date.
    if (documentGaps.length > 0) transactionItems.push({
        labelKey: TranslationKey.CustomerFollowUp.MissingInfo.PaymentTerm,
        optional: true,
        transactions: documentGaps,
    });

    return { clientItems, transactionItems };
}

// A relance is only blocked when a mandatory field is missing. This drives both the
// email-section lock and the banner's visibility, keeping the two perfectly in sync.
export function hasMissingMandatoryInformation(flags: RelanceFlagsDto | null | undefined): boolean {
    const { clientItems, transactionItems } = getMissingInformation(flags);
    return [...clientItems, ...transactionItems].some(item => !item.optional);
}
